import { useRef, useEffect, useMemo, useState } from 'react'
import { TransformControls, Billboard } from '@react-three/drei'
import { useCameraStore } from '../stores/useCameraStore'
import { usePOIHoverStore } from '../stores/usePOIHoverStore'
import { useLoadingStore } from '../stores/useLoadingStore'
import { POI } from '../config/cameraConfig'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { useDevModeStore } from '../stores/useDevModeStore'

// Triangle marker constants
const TRIANGLE_SIZE = 0.55

// Default offset for markers (backward compatible with old hardcoded behavior)
const DEFAULT_OFFSET = { x: 0, y: 2.95, z: 0 } // TIP_OFFSET (2.5) + TRIANGLE_SIZE/2 (0.2)

// Color constants
const HOVER_COLOR = '#63844a' // Green on hover
const HOVER_SCALE = 1.2 // Scale factor on hover

interface POIMarkerProps {
  poi: POI
  onClick: () => void
  onPositionChange?: (position: { x: number; y: number; z: number }) => void
}

function POIMarker({ poi, onClick, onPositionChange }: POIMarkerProps) {
  const markerGroupRef = useRef<THREE.Group>(null)
  const [isHovered, setIsHovered] = useState(false)
  const { editingPOIId } = useCameraStore()
  const hoveredPOIId = usePOIHoverStore((s) => s.hoveredPOIId)
  const setHoveredPOIId = usePOIHoverStore((s) => s.setHoveredPOIId)
  const poiMarkerColor = useDevModeStore((s) => s.poiMarkerColor)
  const poiMarkerOutlineColor = useDevModeStore((s) => s.poiMarkerOutlineColor)
  const poiMarkerOutlineWidth = useDevModeStore((s) => s.poiMarkerOutlineWidth)
  const isBeingEdited = editingPOIId === poi.id
  const isEffectivelyHovered = isHovered || hoveredPOIId === poi.id
  const scaleGroupRef = useRef<THREE.Group>(null)
  const materialRef = useRef<THREE.MeshBasicMaterial>(null)

  useFrame(({ clock }) => {
    if (!scaleGroupRef.current) return
    const current = scaleGroupRef.current.scale.x
    const target = isEffectivelyHovered
      ? HOVER_SCALE
      : 1 + 0.1 * Math.sin(clock.getElapsedTime() * ((2 * Math.PI) / 3)) // ~3s cycle, 10% amplitude
    const smoothed = THREE.MathUtils.lerp(current, target, 0.08)
    scaleGroupRef.current.scale.setScalar(smoothed)

    // Smooth color transition
    if (materialRef.current) {
      const targetColor = isEffectivelyHovered ? HOVER_COLOR : poiMarkerColor
      if (materialRef.current.color.getHexString() !== new THREE.Color(targetColor).getHexString()) {
        materialRef.current.color.lerp(new THREE.Color(targetColor), 0.1)
      }
    }
  })

  // Get offset from POI or use default
  const offset = poi.offset || DEFAULT_OFFSET

  // Create downward-pointing triangle geometry
  const triangleGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry()
    const halfSize = TRIANGLE_SIZE / 2
    // Downward pointing triangle: wide at top, point at bottom (tip)
    const vertices = new Float32Array([
      -halfSize,
      halfSize,
      0, // top-left
      halfSize,
      halfSize,
      0, // top-right
      0,
      -halfSize,
      0, // bottom center (TIP)
    ])
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3))
    return geometry
  }, [])

  // Create outline geometry (line loop uses same vertices)
  const outlineGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry()
    const halfSize = TRIANGLE_SIZE / 2
    const vertices = new Float32Array([
      -halfSize, halfSize, 0,
      halfSize, halfSize, 0,
      0, -halfSize, 0,
    ])
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3))
    return geometry
  }, [])

  return (
    <>
      {/* Transform controls OUTSIDE the group to avoid circular reference */}
      {isBeingEdited && markerGroupRef.current && (
        <TransformControls
          object={markerGroupRef.current}
          mode="translate"
          onObjectChange={() => {
            if (markerGroupRef.current && onPositionChange) {
              const pos = markerGroupRef.current.position
              onPositionChange({
                x: Math.round((pos.x - offset.x) * 100) / 100,
                y: Math.round((pos.y - offset.y) * 100) / 100,
                z: Math.round((pos.z - offset.z) * 100) / 100,
              })
            }
          }}
        />
      )}

      <group
        ref={markerGroupRef}
        position={[poi.target.x + offset.x, poi.target.y + offset.y, poi.target.z + offset.z]}
      >
        {/* Black downward triangle marker with billboard */}
        <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>
          {/* Invisible hitbox for easier clicking */}
          <mesh
            onClick={(e) => {
              e.stopPropagation()
              if (!isBeingEdited) {
                onClick()
              }
            }}
            onPointerOver={(e) => {
              e.stopPropagation()
              if (!isBeingEdited) {
                document.body.style.cursor = 'pointer'
                setIsHovered(true)
                setHoveredPOIId(poi.id)
              }
            }}
            onPointerOut={(e) => {
              e.stopPropagation()
              document.body.style.cursor = 'auto'
              setIsHovered(false)
              setHoveredPOIId(null)
            }}
          >
            <circleGeometry args={[TRIANGLE_SIZE * 0.8, 32]} />
            <meshBasicMaterial transparent opacity={0} />
          </mesh>
          {/* Visible triangle with pulse + hover effects */}
          <group ref={scaleGroupRef}>
            <mesh geometry={triangleGeometry}>
              <meshBasicMaterial
                ref={materialRef}
                color={poiMarkerColor}
                side={THREE.DoubleSide}
              />
            </mesh>
            <lineLoop geometry={outlineGeometry}>
              <lineBasicMaterial color={poiMarkerOutlineColor} linewidth={poiMarkerOutlineWidth} />
            </lineLoop>
          </group>
        </Billboard>
      </group>
    </>
  )
}

export default function POIMarkers() {
  const {
    setCurrentPOI,
    currentPOI,
    pois,
    loadPOIs,
    isLoadingPOIs,
    setTempPOIPosition,
    tempPOIPosition,
    incrementTransitionTrigger,
  } = useCameraStore()
  const { isVisible: isLoadingVisible } = useLoadingStore()

  // Load POIs on component mount
  useEffect(() => {
    loadPOIs()
  }, [loadPOIs])

  // Don't render markers while loading POIs or while loading screen is visible
  if (isLoadingPOIs || pois.length === 0 || isLoadingVisible) {
    return null
  }

  const handleMarkerPositionChange = (
    poiId: string,
    position: { x: number; y: number; z: number }
  ) => {
    // Update temporary position in global store for live preview
    setTempPOIPosition({ id: poiId, target: position })
  }

  return (
    <>
      {pois.map((poi) => {
        // Use temp position if being dragged, otherwise use original
        const displayPOI =
          tempPOIPosition && tempPOIPosition.id === poi.id
            ? { ...poi, target: tempPOIPosition.target }
            : poi

        return (
          <POIMarker
            key={poi.id}
            poi={displayPOI}
            onClick={() => {
              if (currentPOI?.id === poi.id) {
                incrementTransitionTrigger() // Re-trigger camera transition
              } else {
                setCurrentPOI(poi)
              }
            }}
            onPositionChange={(position) => handleMarkerPositionChange(poi.id, position)}
          />
        )
      })}
    </>
  )
}
